import {
  FileSignature, FileStack, FileText, Bank
} from '@huspy/briks-icons';
import { Opportunity } from '@modules/opportunities/entities/opportunity/opportunity.entity';
import {
  CaseCreationBasicInfoRoute,
  CaseCreationBankDocumentsRoute,
  CaseCreationBankFormsRoute,
  CaseCreationSelectBankProductsRoute,
  CaseCreationSubmissionRootRoute,
  CaseCreationFillFormsRoute,
  CaseCreationSubmitApplicationsRoute,
  CaseCreationVaultFillFormsRoute,
  CaseCreationBanksFillFormsRoute
} from '@modules/opportunities/presentation/routes';
import { AnyRoute } from '@tanstack/react-router';
import {
  useMemo, JSX, useCallback
} from 'react';
import { useTranslation } from 'react-i18next';
import { IS_UAE_ENV } from '@modules/core/utils';
import { MORTGAGE_USAGE } from '@modules/opportunities/entities/opportunity/const';
import type { CaseCreationEngine } from '../../../hooks/useCaseCreationStateMachineEngine';

const baseTranslation = 'opportunity.caseSubmission.asideContent';

export namespace CaseCreationAsideLink {
  export type Props<T extends CaseCreationEngine.StateMachineRoutes> = {
    icon: JSX.Element;
    name: string;
    to: T['to'];
    accessibleText: string;
    search?: T['types']['searchSchema'];
    params?: T['types']['allParams'];
    isAccessible: boolean;
    isComplete: boolean;
    subtitle?: string;
    sublinks: {
      name: string;
      to?: T['to'] | `${T['to']}${string}`;
      search: T['types']['searchSchema'];
      params: T['types']['allParams'];
      isAccessible?: boolean;
      isComplete?: boolean;
    }[];
  };

  // eslint-disable-next-line max-len
  export type FactoryArgs<T extends AnyRoute> = Omit<CaseCreationAsideLink.Props<T>, 'to' | 'isAccessible' | 'isComplete' | 'sublinks'> & {
    route: T;
    sublinks: (Omit<CaseCreationAsideLink.Props<T>['sublinks'][number], 'to'> & {
      to?: `${T['to']}${string}`
    })[];
  };
}

const useGetCaseCreationAsideLinks = ({ stateMachine, opportunity }: {
  opportunity?: Opportunity;
  stateMachine: CaseCreationEngine.StateMachine;
}) => {
  const { t } = useTranslation();

  const isCommercial = opportunity?.mortgage_usage === MORTGAGE_USAGE.COMMERCIAL;

  // eslint-disable-next-line max-len
  const linkFactory = useCallback(<T extends CaseCreationEngine.StateMachineRoutes>(
    obj: CaseCreationAsideLink.FactoryArgs<T>): CaseCreationAsideLink.Props<T> => {
    const { route } = obj;
    return {
      ...obj,
      to: route.to,
      isAccessible: stateMachine[route.to]?.isAccessible,
      isComplete: stateMachine[route.to]?.isComplete,
      sublinks: obj.sublinks?.map((sub) => ({
        ...sub,
        isAccessible: stateMachine[sub.to! as keyof CaseCreationEngine.StateMachine]?.isAccessible,
        isComplete: stateMachine[sub.to! as keyof CaseCreationEngine.StateMachine]?.isComplete,
      })),
    };
  }, [stateMachine]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const getBankApplicationFields = () => {
    if (isCommercial) {
      return [];
    }
    if (IS_UAE_ENV) {
      return [
        linkFactory({
          icon: <FileText height={ 20 } width={ 20 } color='inherit' />,
          name: t(`${baseTranslation}.steps.bankForms.title`),
          accessibleText: '',
          route: CaseCreationFillFormsRoute,
          sublinks: [
            {
              name: t(`${baseTranslation}.steps.bankForms.steps.clientVault.name`),
              params: { opportunityId: opportunity?.opportunity_external_id! },
              to: CaseCreationVaultFillFormsRoute.to,
              search: {},
            },
            {
              name: t(`${baseTranslation}.steps.bankForms.steps.remainingFields.name`),
              params: { opportunityId: opportunity?.opportunity_external_id! },
              to: CaseCreationBanksFillFormsRoute.to,
              search: {},
            }
          ],
        })
      ];
    }
    return [
      linkFactory({
        icon: <FileText height={ 20 } width={ 20 } color='inherit' />,
        name: t(`${baseTranslation}.steps.bankForms.title`),
        accessibleText: '',
        route: CaseCreationFillFormsRoute,
        sublinks: [
          {
            name: t(`${baseTranslation}.steps.bankForms.steps.clientVault.name`),
            params: { opportunityId: opportunity?.opportunity_external_id! },
            to: CaseCreationVaultFillFormsRoute.to,
            search: {},
          }
        ],
      })
    ];
  };

  const getBankSubmissionSublinks = () => {
    if (isCommercial) {
      return [
        {
          name: t(`${baseTranslation}.steps.bankSubmissions.steps.bankForms.name`),
          params: { opportunityId: opportunity?.opportunity_external_id },
          to: CaseCreationBankFormsRoute.to,
          search: {},
        },
        {
          name: t(`${baseTranslation}.steps.bankSubmissions.steps.submit.name`),
          to: CaseCreationSubmitApplicationsRoute.to,
          params: { opportunityId: opportunity?.opportunity_external_id },
          search: {},
        }
      ];
    }
    return [
      {
        name: t(`${baseTranslation}.steps.bankSubmissions.steps.bankProducts.name`),
        params: { opportunityId: opportunity?.opportunity_external_id! },
        to: CaseCreationSelectBankProductsRoute.to,
        search: {},
      },
      {
        name: t(`${baseTranslation}.steps.bankSubmissions.steps.bankForms.name`),
        params: { opportunityId: opportunity?.opportunity_external_id! },
        to: CaseCreationBankFormsRoute.to,
        search: {},
      },
      {
        name: t(`${baseTranslation}.steps.bankSubmissions.steps.submit.name`),
        to: CaseCreationSubmitApplicationsRoute.to,
        params: { opportunityId: opportunity?.opportunity_external_id! },
        search: {},
      }
    ];
  };

  const asideLinks = useMemo(() => [
    linkFactory({
      route: CaseCreationBasicInfoRoute,
      icon: <FileSignature height={ 20 } width={ 20 } color='inherit' />,
      name: t(`${baseTranslation}.steps.basicInfo.title`),
      accessibleText: t(`${baseTranslation}.pending`),
      // eslint-disable-next-line max-len
      subtitle: `${stateMachine[CaseCreationBasicInfoRoute.to].stateMetadata?.completedSteps}/${stateMachine[CaseCreationBasicInfoRoute.to].stateMetadata?.totalSteps} fields`,
      sublinks: [],
    }),
    linkFactory({
      route: CaseCreationBankDocumentsRoute,
      icon: <FileStack height={ 20 } width={ 20 } color='inherit' />,
      name: t(`${baseTranslation}.steps.bankDocuments.title`),
      accessibleText: t(`${baseTranslation}.pending`),
      // eslint-disable-next-line max-len
      subtitle: `${stateMachine[CaseCreationBankDocumentsRoute.to].stateMetadata?.completedSteps}/${stateMachine[CaseCreationBankDocumentsRoute.to].stateMetadata?.totalSteps} documents`,
      sublinks: opportunity?.applicants.map((applicant, idx) => ({
        name: `${applicant.first_name ?? ''} ${applicant.last_name ?? ''}`,
        params: { opportunityId: opportunity?.opportunity_external_id },
        // eslint-disable-next-line max-len
        search: { applicant: idx === 0 ? 'main' : 'additional' as typeof CaseCreationBankDocumentsRoute['types']['searchSchema']['applicant'] },
      }))!,
    }),
    ...(getBankApplicationFields()),
    linkFactory({
      icon: <Bank height={ 20 } width={ 20 } color='inherit' />,
      name: t(`${baseTranslation}.steps.bankSubmissions.title`),
      accessibleText: t(`${baseTranslation}.pending`),
      route: CaseCreationSubmissionRootRoute,
      sublinks: getBankSubmissionSublinks(),
    })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [
    linkFactory,
    t,
    stateMachine,
    opportunity?.applicants,
    opportunity?.opportunity_external_id,
    opportunity?.mortgage_usage,
    getBankApplicationFields
  ]);

  return { asideLinks };
};

export default useGetCaseCreationAsideLinks;
